import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useSelector } from 'react-redux';

import { useRemScale } from 'lib/remScale';
import { TYPE, FONTS } from 'style/typography';
import * as api from 'utils/relicVault/collectionApi';
import {
  advance,
  elsewhereGuide,
  initialStep,
  JOURNEY_VERSION,
  LOOK_GIVE_UP_MS,
  onArrive,
  PLAN_WAIT_MS,
  portalLabel,
  RECOVER_MS,
  recoverDelay,
  recoverStep,
  samePortal,
  stepById,
  STEPS,
  targetOf,
  upgradeStepFor,
  resumeStep,
} from './journey';
import { loadTour, tourEntryFor } from './tourContent';
import { readProgress, startReplay, writeProgress } from './progress';
import { loadAccountState, saveStep } from './accountState';
import { TARGET, findTarget, startLessonTarget, testIdOf, unitTeleportTarget } from './targets';
import { listenInWorldFrame } from './worldFrame';
import {
  consumeTutorSwitchEdge,
  emit,
  on,
  openCourseMenu,
  quikbaseLanding,
  setGuidedVisit,
  setFirstVisitLearner,
  setLeaveHome,
  setMenuInterceptor,
  setPortalHeld,
  setTourGate,
  setTourTutorStep,
  setWorldGuide,
  setTourHide,
  setTourSuggestions,
} from './events';
import { setKeyGate } from './keyGate';
import { badgeMomentStarted } from './badgeWait';
import { onboardingLessonId } from './onboardingContent';
import { LookHint, MoveKeysPad, NextStepCard, Spotlight, TourQuizBoard } from './web/TourPieces';
import FocusRing from './components/FocusRing';
import CoachCard from './components/CoachCard';
import TourQuizPanel from './components/TourQuizPanel';
import TourDonePanel, { prefetchTourBadges } from './components/TourDonePanel';
import CoinBurst from './components/CoinBurst';
import { flowParam, track } from './analytics';
import { getWebHomeUrl } from 'utils/webRedirects';

// A course with no tour content (held for a data fix: Sol realjourney Q3 C) skips the short slide and quiz;
// the rest of the visit still runs.
const CONTENT_STEPS = new Set([
  'open_menu', 'start_tour_lesson', 'tour_slide', 'slide_badge', 'quiz_menu', 'start_tour_quiz', 'tour_quiz', 'quiz_badge',
]);
const TOUR_TUTOR_STEPS = new Set(['tutor_open', 'tutor_answer']);
const REAL_LESSON_STEPS = new Set(['real_menu', 'start_real_lesson', 'real_lesson']);
const DRAG_PX = 50;
const FIRST_FRIEND_READ_MS = 2500;

const nextIndex = id => STEPS.findIndex(step => step.id === id) + 1;

// The owner's prototype (Sol proto-port P1 A): in the course world one "What's next" card names the control that pulses;
// in Quikbase a coach card "Step x of 7" stands beside it with the screen dimmed around it, and the on-screen keys.
const NEXT_CARD = {
  open_menu: ['menu', 'Open All Lessons and Quizzes'],
  store_open: ['store', 'Open your Collection Store'],
  tutor_open: ['tutor', 'Ask {tutor} a question'],
  tutor_close: ['tutor', 'Click Tutor to finish talking'],
  quiz_menu: ['quiz', 'Take a quick quiz'],
  real_menu: ['menu', 'Start your first full lesson'],
  upgrade_open: ['upgrade', 'See everything you can learn'],
  leave: ['leave', 'Leave the unit'],
};
const KEY_STEPS = {
  walk_w: [1, 'W', 'Press W on your keyboard', 'W walks forward.'],
  walk_a: [2, 'A', 'Press A on your keyboard', 'A walks left.'],
  walk_s: [3, 'S', 'Press S on your keyboard', 'S walks back.'],
  walk_d: [4, 'D', 'Press D on your keyboard', 'D walks right.'],
  jump: [5, 'Space', 'Press Space on your keyboard', 'Space makes you jump.'],
};
const FLY_STEPS = {
  fly_on: [TARGET.tileFly, 'Fly', 'Click Fly to take off.'],
  fly_up: [TARGET.flyUp, 'Fly higher', 'Click Up to rise.'],
  fly_down: [TARGET.flyDown, 'Fly lower', 'Click Down to come down.'],
  fly_off: [TARGET.tileFly, 'Land', 'Click Fly again to land.'],
};
// the Quikbase tour's steps: the on-screen keys show from the first key to the portal, and never after (owner 15.4)
const QUIKBASE_TOUR = ['walk_w', 'walk_a', 'walk_s', 'walk_d', 'jump', 'look', 'fly_on', 'fly_up', 'fly_down', 'fly_off', 'controls_badge', 'go_portal'];

/**
 * The tour's own parts (the tour course world, then the Quikbase controls): only the step's control works there, every
 * other world button says "Available after the tour" (owner round 4; Sol onboarding-leftovers L2 A). The first real
 * lesson after them is not gated.
 */
const GATED_PARTS = [
  ['open_menu', 'tour_done'],
  ['walk_w', 'controls_badge'],
];
const GATE_ALLOW = {
  open_menu: [TARGET.lessonsButton],
  quiz_menu: [TARGET.lessonsButton],
  store_open: [TARGET.collectionStore],
  tutor_open: [TARGET.tileTutor],
  tutor_answer: [TARGET.tileTutor],
  tutor_close: [TARGET.tileTutor],
  fly_on: [TARGET.tileFly],
  fly_up: [TARGET.flyUp, TARGET.tileFly],
  fly_down: [TARGET.flyDown, TARGET.tileFly],
  fly_off: [TARGET.tileFly],
};
const inGatedPart = stepId => {
  const at = STEPS.findIndex(s => s.id === stepId);
  return GATED_PARTS.some(([from, to]) => at >= STEPS.findIndex(s => s.id === from) && at <= STEPS.findIndex(s => s.id === to));
};

/**
 * The guided first visit over the 3D world (owner 2026-10-09; Sol realjourney Q1 A, player-tour Q1-Q4). It shows
 * one thing at a time - a key to press, the green hand and outline on what to click, the short slide or quiz - and
 * moves on when the learner does it. It follows the page (keys, clicks, drags) and the world's state; it never
 * presses anything for the learner.
 *
 * Who gets it: accounts created on or after the release (the server says), or anyone who chose "Replay tour".
 */
export default function FirstVisitTour({
  token,
  userId,
  curriculumId,
  place,
  ready,
  storeOpen,
  tutorOn,
  lessonStarted,
  lessonMenuOpen,
  confirmOpen,
  coachTop,
}) {
  const { rem } = useRemScale();
  const plansOpen = useSelector(state => Boolean(state.subscriptionUi?.visible));
  const plan = useSelector(state => state.subscriptionUi?.plan || null);
  const unitMenuOpen = useSelector(state => Boolean(state.course?.showUnitSelectionScreen));
  const menuCourse = useSelector(state => state.course?.curriculumGradeAndSubject || null);
  // Set when the server refused a session (minutes used up, unit outside the plan) and the plans opened instead.
  const refused = useSelector(state => Boolean(state.subscriptionUi?.exhausted));
  const ownedBadgeIds = useSelector(state => state.relicVault?.ownedBadgeIds || []);
  const lessonProgress = useSelector(state => state.course?.currentLessonProgress || {});

  const [status, setStatus] = useState(null);
  // the account's onboarding state: undefined while it loads, null when the server cannot be reached (this browser's
  // values then lead, as before - Sol onboarding-account-state D5 A)
  const [account, setAccount] = useState(undefined);
  const [entry, setEntry] = useState(undefined);
  const [step, setStep] = useState(null);
  const [badgePlaying, setBadgePlaying] = useState(false);
  const replay = useRef(false);
  // Where the learner landed in Quikbase: in front of the course portal, or somewhere it is not in view.
  const [landing, setLanding] = useState(() => quikbaseLanding());
  useEffect(() => on('quikbase-landing', value => setLanding(value)), []);

  // --- who gets it, where they are, and this course's content -------------------------------------------------
  useEffect(() => {
    if (!token || !userId) return;
    let cancelled = false;
    const local = readProgress(userId);
    replay.current = !!local?.replay;
    let tutorialSeen = false;
    try {
      tutorialSeen = window.localStorage.getItem(`quiklearn_tutorial_video_seen_${userId}`) === 'true';
    } catch (_) {
      tutorialSeen = false;
    }
    loadAccountState({ token, local: { progress: local, tutorialSeen } }).then(view => {
      if (cancelled) return;
      if (view) replay.current = Boolean(view.tourReplay) || replay.current;
      setAccount(view || null);
    });
    api
      .fetchFirstVisitStatus({ token })
      .catch(() => ({ eligible: false, steps: {} }))
      .then(result => {
        if (cancelled) return;
        setFirstVisitLearner(Boolean(result?.eligible));
        setStatus(result || { eligible: false, steps: {} });
      });
    return () => {
      cancelled = true;
    };
  }, [token, userId]);

  useEffect(() => {
    if (place !== 'world') return;
    let cancelled = false;
    loadTour()
      .then(tour => !cancelled && setEntry(tourEntryFor(tour, curriculumId)))
      .catch(() => !cancelled && setEntry(null));
    return () => {
      cancelled = true;
    };
  }, [curriculumId, place]);

  const active = Boolean(status && (status.eligible || replay.current) && ready && place);

  // Tell the world whether the visit runs, so its own first-time videos stay closed meanwhile (Sol tour-collisions Q1 A).
  useEffect(() => {
    if (!status) return;
    setGuidedVisit(Boolean((status.eligible || replay.current) && step !== 'done'));
  }, [status, step]);

  // "Replay the guided tour" (Help modal; Sol tour-collisions Q2 A): start again from the Quikbase controls. From a
  // course world that means teleporting to Quikbase first; the visit picks up there.
  useEffect(
    () =>
      on('replay-tour', () => {
        if (!userId) return;
        startReplay(userId);
        saveStep('walk_w', { replayStart: true });
        replay.current = true;
        setStatus(current => current || { eligible: false, steps: {} });
        setStep('walk_w');
        if (place !== 'quikbase') window.dispatchEvent(new Event('quiklearn:teleport-quikbase'));
      }),
    [place, userId],
  );

  // Skip the slide and quiz where this course has no content (only once we know it has none).
  const settle = useCallback(
    stepId => {
      let id = stepId;
      while (id && entry === null && CONTENT_STEPS.has(id)) id = STEPS[nextIndex(id)].id;
      return id;
    },
    [entry],
  );

  useEffect(() => {
    if (!active || step || account === undefined) return;
    // where the learner is: the account first (any device), else this browser, else from the badges held
    const stored = account?.tourStep || readProgress(userId)?.step;
    setStep(settle(onArrive(stored ? resumeStep(stored, status.steps) : initialStep(status.steps, place), place)));
  }, [account, active, place, settle, status, step, userId]);

  useEffect(() => {
    if (step) setStep(current => settle(current));
  }, [entry, settle, step]);

  useEffect(() => {
    if (step && userId) {
      writeProgress(userId, { step, replay: replay.current });
      saveStep(step);
    }
  }, [step, userId]);

  const lessonId = entry?.lessonId || '';
  const dispatch = useCallback(
    event => setStep(current => (current ? settle(advance(current, event, lessonId)) : current)),
    [lessonId, settle],
  );

  const current = step ? stepById(step) : null;
  const here = Boolean(active && current && current.id !== 'done' && current.place === place);

  // Quikbase controls (owner round 14.6; Sol dev-port D2 A): while its steps run, only the asked key reaches the world,
  // one fixed step per press; none on the look and fly steps; every key on the way to the portal.
  const quikbaseTour = JOURNEY_VERSION === 2 && here && place === 'quikbase' && QUIKBASE_TOUR.includes(current.id);
  const keyAllow = !quikbaseTour ? undefined : current.id === 'go_portal' ? null : KEY_STEPS[current.id]?.[1] || 'none';
  useEffect(() => {
    setKeyGate(quikbaseTour ? { armed: true, allow: keyAllow } : { armed: false });
  }, [keyAllow, quikbaseTour]);
  useEffect(() => () => setKeyGate({ armed: false }), []);

  // The look step moves on by itself (owner round 13.10); its 10 QuikCoins are only for a drag that happened.
  const unpaid = useRef(null);
  useEffect(() => {
    if (!here || current.id !== 'look') return undefined;
    const timer = setTimeout(() => {
      unpaid.current = 'look';
      dispatch('look:timeout');
    }, LOOK_GIVE_UP_MS);
    return () => clearTimeout(timer);
  }, [current, dispatch, here]);

  // The tutor's microphone step is on screen: the guide steps back meanwhile (owner 11 Oct, voice-first).
  const [micOpen, setMicOpen] = useState(false);
  useEffect(() => on('mic-gate', open => setMicOpen(!!open)), []);

  // The course portal's menu waits until "Walk into the glowing portal" (Sol tour-portal-spawn A); any other state -
  // no visit, a later step, the visit over or this component gone - leaves portals working as usual.
  // Without a portal walk (the designed order ends at the controls badge) the portal is held until the visit is over.
  const portalAt = STEPS.findIndex(s => s.id === 'go_portal');
  const holdPortal = Boolean(active && place === 'quikbase' && current && current.place === 'quikbase' &&
    STEPS.findIndex(s => s.id === current.id) < (portalAt < 0 ? STEPS.length : portalAt));
  useEffect(() => {
    setPortalHeld(holdPortal);
  }, [holdPortal]);
  useEffect(() => () => setPortalHeld(false), []);

  // --- the page: keys, clicks and drags ---------------------------------------------------------------------------
  useEffect(() => {
    if (!here || Platform.OS !== 'web' || typeof window === 'undefined') return undefined;
    const typing = target =>
      target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName || ''));
    // A key can reach the tour twice (from the page and from inside the world frame); count one press once.
    let lastKey = { code: '', at: 0 };
    const onKey = event => {
      if (event.repeat || typing(event.target)) return;
      const now = Date.now();
      if (event.code === lastKey.code && now - lastKey.at < 50) return;
      lastKey = { code: event.code, at: now };
      dispatch(`key:${event.code}`);
    };
    const onClick = event => {
      const id = testIdOf(event.target);
      if (id) dispatch(`click:${id}`);
    };
    let drag = null;
    const onDown = event => {
      drag = event.button === 0 && event.target?.tagName === 'CANVAS' ? { x: event.clientX, y: event.clientY, done: false } : null;
    };
    const onMove = event => {
      if (!drag || drag.done) return;
      if (Math.hypot(event.clientX - drag.x, event.clientY - drag.y) >= DRAG_PX) {
        drag.done = true;
        dispatch('drag');
      }
    };
    const onUp = () => {
      drag = null;
    };
    // Capture phase, never preventDefault: the world and the buttons still get every key, click and drag.
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('click', onClick, true);
    window.addEventListener('pointerdown', onDown, true);
    window.addEventListener('pointermove', onMove, true);
    window.addEventListener('pointerup', onUp, true);
    // Keys pressed and drags made while the world has focus happen inside its iframe, out of the page's sight.
    const stopFrame = listenInWorldFrame({ keydown: onKey, pointerdown: onDown, pointermove: onMove, pointerup: onUp });
    return () => {
      stopFrame();
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('click', onClick, true);
      window.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('pointermove', onMove, true);
      window.removeEventListener('pointerup', onUp, true);
    };
  }, [dispatch, here]);

  // --- the world: state changes become events --------------------------------------------------------------------
  const previous = useRef({});
  useEffect(() => {
    const before = previous.current;
    previous.current = { storeOpen, tutorOn, plansOpen, confirmOpen, lessonStarted, lessonMenuOpen };
    if (!here) return undefined;
    let closedTimer = null;
    if (!before.lessonMenuOpen && lessonMenuOpen) dispatch('menu:opened');
    // A Start press closes the menu as it starts the slide, quiz or lesson; give that a moment to arrive first.
    if (before.lessonMenuOpen && !lessonMenuOpen) closedTimer = setTimeout(() => dispatch('menu:closed'), 800);
    if (before.storeOpen && !storeOpen) dispatch('store:closed');
    // "Type instead" / "Talk instead" stops one session and starts another: neither edge is the learner's.
    if (!before.tutorOn && tutorOn && !consumeTutorSwitchEdge()) dispatch('tutor:started');
    if (before.tutorOn && !tutorOn && !consumeTutorSwitchEdge()) dispatch('tutor:stopped');
    if (!before.plansOpen && plansOpen) dispatch('plans:opened');
    if (before.plansOpen && !plansOpen) dispatch('plans:closed');
    if (!before.confirmOpen && confirmOpen) dispatch('leave:opened');
    if (!before.lessonStarted && lessonStarted) dispatch('lesson:started');
    return () => closedTimer && clearTimeout(closedTimer);
  }, [confirmOpen, dispatch, here, lessonMenuOpen, lessonStarted, plansOpen, storeOpen, tutorOn]);

  // The first real lesson is done when the server awards First Steps, or the lesson reaches 100%. Checked on the
  // steps around it too: a lesson that ends before its completion arrives sends the tour back to the menu first.
  useEffect(() => {
    if (!here || !REAL_LESSON_STEPS.has(current.id)) return;
    const finishedLesson =
      lessonProgress.currentLessonId === lessonId && Number(lessonProgress.currentLessonProgressPercentage) >= 100;
    if (ownedBadgeIds.includes('EXP-001') || finishedLesson) dispatch('lesson:completed');
  }, [current, dispatch, here, lessonId, lessonProgress, ownedBadgeIds]);

  // First Friend is awarded by the server when the session starts: read the vault so its moment plays.
  useEffect(() => {
    if (!tutorOn || !here) return undefined;
    const timer = setTimeout(() => window.dispatchEvent(new Event('quikcoins:balance')), FIRST_FRIEND_READ_MS);
    return () => clearTimeout(timer);
  }, [here, tutorOn]);

  // (the tour's two tutor sessions end themselves after their first answer - Controls, Sol dev-acc-3)
  useEffect(() => on('tutor-answered', () => dispatch('tutor:answered')), [dispatch]);
  // The lesson played to its end (or reached its closing tutor question): done at once, before its progress and
  // badge arrive (about 9 s later on DEV), so the stop is never read as leaving the lesson.
  useEffect(() => on('lesson-finished', () => dispatch('lesson:completed')), [dispatch]);
  const badgePlayingNow = useRef(false);
  useEffect(() => on('badge-moment', ({ playing }) => {
    badgePlayingNow.current = !!playing;
    setBadgePlaying(!!playing);
  }), []);

  // The tour's own lesson and quiz replace the menu's Start Lesson / Start Quiz for the tour lesson only.
  useEffect(
    () =>
      setMenuInterceptor((kind, pressedLessonId) => {
        if (!here || !lessonId || pressedLessonId !== lessonId) return false;
        if (current.id === 'start_tour_lesson' && kind === 'lesson') {
          // The tour's own slide plays on the in-world board as a lesson (owner 13 Oct; Sol owner-r27 Q2 A) when the world
          // loaded the onboarding copy of this unit; otherwise the real lesson's slide plays, as before (Sol owner-r24-tour-slide A).
          dispatch('slide:started');
          const onboarding = onboardingLessonId();
          return onboarding ? { lessonId: onboarding } : false;
        }
        if (current.id === 'start_tour_quiz' && kind === 'quiz') {
          dispatch('quiz:started');
          return true;
        }
        return false;
      }),
    [current, dispatch, here, lessonId],
  );

  // The tour's slide is the real lesson's first slide on the board (Sol owner-r24-tour-slide A): it is done when the
  // lesson moves on to the next slide or ends; the lesson is then ended once (Meeting runs its normal exit, keeping
  // progress) and the Slide Explorer badge follows. "exits exactly once on index 1 or lesson end".
  const slidesWatched = useSelector(state => Number(state.course?.currentLessonProgress?.currentLessonSlidesWatched) || 0);
  const slideFrom = useRef(null);
  const slideFinished = useRef(false);
  const finishTourSlide = useCallback(() => {
    if (slideFinished.current) return;
    slideFinished.current = true;
    emit('tour-end-lesson');
    dispatch('slide:done');
  }, [dispatch]);
  useEffect(() => {
    if (!here || current.id !== 'tour_slide') {
      slideFrom.current = null;
      slideFinished.current = false;
      return;
    }
    if (slideFrom.current === null) slideFrom.current = slidesWatched;
    if (slidesWatched >= Math.max(2, slideFrom.current + 1)) finishTourSlide();
  }, [current, finishTourSlide, here, slidesWatched]);
  useEffect(
    () => on('lesson-finished', () => {
      if (here && current?.id === 'tour_slide') finishTourSlide();
    }),
    [current, finishTourSlide, here],
  );

  // Exit goes Home in the last step (owner: Upgrade, close it, then Leave to Home).
  useEffect(() => {
    setLeaveHome(Boolean(here && current.id === 'leave'));
    return () => setLeaveHome(false);
  }, [current, here]);

  // A badge step reports to the server, which awards and pays once; reading the vault plays the badge moment.
  useEffect(() => {
    if (!here || current.kind !== 'badge') return undefined;
    let cancelled = false;
    const report = async () => {
      for (let attempt = 0; attempt < 3 && !cancelled; attempt += 1) {
        try {
          await api.reportFirstVisitStep({ token, step: current.step });
          break;
        } catch (error) {
          console.warn(`[firstVisit] reporting ${current.step} failed`, error?.message);
          await new Promise(resolve => setTimeout(resolve, 1200 * (attempt + 1)));
        }
      }
      if (cancelled) return;
      window.dispatchEvent(new Event('quikcoins:balance'));
      // The badge moment follows the report by a second or two (the vault is read again): the step moves on once it has
      // begun - or after BADGE_MOMENT_WAIT_MS when none comes (a badge already held) - so the next step's panel never shows
      // before it (owner 13 Oct: "the animation for this card [You did it!] ... was already there even before quiz card or
      // badge was shown"; measured on DEV: You did it! 1.8 s before the Quiz Starter moment).
      await badgeMomentStarted(() => badgePlayingNow.current);
      if (cancelled) return;
      dispatch('badge:reported');
    };
    report();
    return () => {
      cancelled = true;
    };
  }, [current, dispatch, here, token]);

  // The plans are already open at "Click Upgrade" (the lesson's tutor ran out of minutes and opened them, with "Use
  // Chat"): that is the step done, so the next one is "Close this for now" rather than a button hidden behind them.
  useEffect(() => {
    if (here && current?.id === 'upgrade_open' && plansOpen) dispatch('plans:opened');
  }, [current, dispatch, here, plansOpen]);

  // Only a Free plan has the Upgrade button: skip to Exit when the plan says otherwise or has not loaded within
  // PLAN_WAIT_MS (Sol tour-upgrade-step B). A plan that arrives Free in time clears the timer and keeps the step.
  useEffect(() => {
    if (!here || !current || current.id !== 'upgrade_open') return undefined;
    const decision = upgradeStepFor(plan, 0);
    if (decision === 'skip') {
      dispatch('upgrade:unavailable');
      return undefined;
    }
    if (decision === 'show') return undefined;
    const timer = setTimeout(() => dispatch('upgrade:unavailable'), PLAN_WAIT_MS);
    return () => clearTimeout(timer);
  }, [current, dispatch, here, plan]);

  // No dead ends (owner 2026-10-10; Sol tour-dead-ends B): a step whose panel, tutor or lesson is gone - after a
  // reload, a return from checkout, or the learner closing it - moves to where the learner really is.
  const ui = { store: !!storeOpen, menu: !!lessonMenuOpen, tutor: !!tutorOn, lesson: !!lessonStarted, plans: !!plansOpen };
  const recoverTo = here && current ? recoverStep(current.id, ui) : null;
  useEffect(() => {
    if (!current || !recoverTo || recoverTo === current.id) return undefined;
    const from = current.id;
    const timer = setTimeout(() => setStep(now => (now === from ? settle(recoverTo) : now)), recoverDelay(from));
    return () => clearTimeout(timer);
  }, [current, recoverTo, settle]);

  // The tutor was refused and the plans opened instead: go on to the quiz rather than ask for the tutor again.
  useEffect(() => {
    if (here && current?.id === 'tutor_open' && refused) dispatch('tutor:refused');
  }, [current, dispatch, here, refused]);

  // This unit's menu has lessons but not the tour's (a later unit): no tour slide or quiz here.
  useEffect(() => {
    if (!here || !lessonMenuOpen || !lessonId || !['start_tour_lesson', 'start_tour_quiz'].includes(current?.id)) {
      return undefined;
    }
    const timer = setTimeout(() => {
      const menuHasLessons = typeof document !== 'undefined' && document.querySelector('[data-testid^="lesson-menu-start-lesson-"]');
      if (menuHasLessons && !findTarget(startLessonTarget(lessonId))) setEntry(null);
    }, RECOVER_MS);
    return () => clearTimeout(timer);
  }, [current, here, lessonId, lessonMenuOpen]);

  // The tour's tutor question is free once per learner (Sol tour-question-unmetered A): say so while on its steps.
  const onTourTutorStep = Boolean(here && current && TOUR_TUTOR_STEPS.has(current.id));
  useEffect(() => {
    setTourTutorStep(onTourTutorStep);
    return () => setTourTutorStep(false);
  }, [onTourTutorStep]);
  // ... and offers its course's own questions on the tutor's bar (the prototype's suggestions; Try saying with a mic).
  const tourQuestions = entry?.tutorChat?.suggestions;
  useEffect(() => {
    setTourSuggestions(onTourTutorStep && tourQuestions ? tourQuestions.map(item => item.question) : null);
    return () => setTourSuggestions(null);
  }, [onTourTutorStep, tourQuestions]);

  // "Skip tour" on every coach card ends the visit; it is stored like any step, so a reload keeps it ended.
  // "Explore more worlds" on "You did it!" goes to the portal's "Which subject first?" (owner rounds 10-12).
  // The visit's analytics id goes along, so the portal's events join the same funnel (Sol onboarding-leftovers L5 A).
  const goToSubject = useCallback(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.location.href = `${getWebHomeUrl()}/onboarding/subject?flow=${encodeURIComponent(flowParam())}`;
    }
  }, []);
  const exploreMore = useCallback(() => {
    track('explore_more_clicked', { tour_variant: entry?.lessonId || null });
    goToSubject();
  }, [entry, goToSubject]);
  // SKIP TOUR ends all guidance and rewards. Skipped in the tour course world (before Quikbase) it goes on to the
  // portal's "Which subject first?", as the owner's prototype does, and Quikbase then loads unguided; anywhere else the
  // learner stays where they are (Sol onboarding-dev-acc-1 E1 A, replacing onboarding-build-2 D3 B). The end is stored
  // before the page changes.
  const skipTour = useCallback(() => {
    const beforeControls = STEPS.findIndex(s => s.id === step) < STEPS.findIndex(s => s.id === 'walk_w');
    if (userId) writeProgress(userId, { step: 'done', replay: replay.current });
    saveStep('done');
    track('tour_skipped', { from_step: step });
    setStep('done');
    if (JOURNEY_VERSION === 2 && place === 'world' && beforeControls) goToSubject();
  }, [goToSubject, place, step, userId]);
  const coachSkip = JOURNEY_VERSION === 2 ? undefined : skipTour;

  // Funnel events: where the visit is and which step it shows (Sol analytics + round12 E5).
  const arrivedAt = useRef({});
  useEffect(() => {
    if (!active || !place || arrivedAt.current[place]) return;
    arrivedAt.current[place] = true;
    track(place === 'quikbase' ? 'quikbase_arrived' : 'onboarding_lab_loaded', { tour_variant: entry?.lessonId || null });
  }, [active, entry, place]);
  useEffect(() => {
    if (here && current) track('tour_step_viewed', { step_id: current.id, place });
  }, [current, here, place]);
  useEffect(() => {
    if (here && current?.id === 'go_portal' && unitMenuOpen) track('unit_selector_viewed', {});
  }, [current, here, unitMenuOpen]);

  // Gate the world's buttons while one of the tour's own parts runs here; lift it everywhere else. The step's own target
  // always works - in the lesson menu that is the tour lesson's Start Lesson / Start Quiz, and nothing else there (owner
  // 13 Oct: "I clicked 2nd lesson and it was selectable messing tour state"; Sol owner-r27 Q4 A).
  const gated = JOURNEY_VERSION === 2 && here && inGatedPart(current.id);
  const gateKey = gated ? current.id : '';
  const stepTarget = gated ? targetOf(current, lessonId) : '';
  useEffect(() => {
    setTourGate(gateKey ? [...(GATE_ALLOW[gateKey] || []), stepTarget].filter(Boolean) : null);
  }, [gateKey, stepTarget]);
  useEffect(() => () => setTourGate(null), []);
  const [blockedTip, setBlockedTip] = useState(null);
  useEffect(
    () =>
      on('tour:blocked', testId => {
        const element = findTarget(testId);
        const rect = element ? element.getBoundingClientRect() : null;
        if (rect) setBlockedTip({ key: `${testId}-${Date.now()}`, rect });
      }),
    [],
  );
  useEffect(() => {
    if (!blockedTip) return undefined;
    const timer = setTimeout(() => setBlockedTip(null), 1600);
    return () => clearTimeout(timer);
  }, [blockedTip]);

  // ... and the HUD hides what the prototype hides there (Sol proto-port P2 A): only during the course-world tour part.
  const hideHud = JOURNEY_VERSION === 2 && gated && place === 'world';
  useEffect(() => {
    setTourHide(hideHud);
  }, [hideHud]);
  useEffect(() => () => setTourHide(false), []);

  // While the tour leads in the course world the course box stays folded (Sol onboarding-dev-acc-1 E2 A).
  const leadingInWorld = JOURNEY_VERSION === 2 && Boolean(active && current && current.id !== 'done' && place === 'world');
  useEffect(() => {
    setWorldGuide(leadingInWorld);
  }, [leadingInWorld]);
  useEffect(() => () => setWorldGuide(false), []);

  // "You did it!" shows its badge pictures at once: the badge list is fetched when the tour starts (E4 A).
  useEffect(() => {
    if (JOURNEY_VERSION === 2 && active && token) prefetchTourBadges({ token });
  }, [active, token]);

  // With a menu open, the guide sits just under the menu's header so its title stays readable (E3 A).
  const [menuHeaderBottom, setMenuHeaderBottom] = useState(null);
  useEffect(() => {
    if (Platform.OS !== 'web' || !(lessonMenuOpen || unitMenuOpen)) {
      setMenuHeaderBottom(null);
      return undefined;
    }
    const measure = () => {
      const header = findTarget(unitMenuOpen ? TARGET.unitMenuHeader : TARGET.lessonMenuHeader);
      const bottom = header ? Math.round(header.getBoundingClientRect().bottom) : 0;
      setMenuHeaderBottom(current => (bottom > 0 ? (current === bottom ? current : bottom) : null));
    };
    measure();
    const timer = setInterval(measure, 400);
    return () => clearInterval(timer);
  }, [lessonMenuOpen, unitMenuOpen]);
  const guideTop = menuHeaderBottom ? menuHeaderBottom + rem(8) : coachTop;

  // A control the server pays for (10 QuikCoins once each; Sol onboarding-build Q2 A): reported when its step is done
  // in order - never on a skip or a recovery jump.
  // The server's answer decides the coins shown: a burst only when it paid now (owner 10-10: the same animation and coin
  // sound as the badges).
  const [coinBurst, setCoinBurst] = useState(null);
  const clearCoinBurst = useCallback(() => setCoinBurst(null), []);
  const previousStep = useRef(null);
  useEffect(() => {
    const before = previousStep.current;
    previousStep.current = step;
    const inOrder = before && step && STEPS.findIndex(s => s.id === step) === STEPS.findIndex(s => s.id === before) + 1;
    if (inOrder) track('tour_step_completed', { step_id: before });
    if (before === 'go_portal' && step !== 'done') track('teleport_completed', {});
    let control = inOrder ? stepById(before)?.pays : null;
    if (control && unpaid.current === control) {
      unpaid.current = null;
      control = null;
    }
    if (!control || !token) return;
    api
      .reportFirstVisitControl({ token, control })
      .then(result => {
        const paid = Number(result?.coinsPaid) || 0;
        track('quikbase_control_used', { control_id: control, coins: paid });
        if (paid > 0) setCoinBurst({ amount: paid, key: `${control}-${Date.now()}` });
        window.dispatchEvent(new Event('quikcoins:balance'));
      })
      .catch(error => console.warn(`[firstVisit] reporting control ${control} failed`, error?.message));
  }, [step, token]);

  // A tile hidden in the collapsed controls palette: open the palette.
  const [tutorChoiceOpen, setTutorChoiceOpen] = useState(false);
  useEffect(() => {
    if (!here || !current || current.id !== 'tutor_open' || Platform.OS !== 'web') {
      setTutorChoiceOpen(false);
      return undefined;
    }
    const look = () => setTutorChoiceOpen(Boolean(findTarget(TARGET.tutorModeChoice)));
    look();
    const timer = setInterval(look, 300);
    return () => clearInterval(timer);
  }, [current, here]);

  const openControls = useCallback(targetId => {
    if (String(targetId).startsWith('control-')) window.dispatchEvent(new Event('quiklearn:open-controls'));
  }, []);

  // The simplified first visit (DEV): the owner's prototype, piece by piece (Sol proto-port P1-P6 A, dev-port D1-D4 A).
  const renderGuideV2 = () => {
    const myPortal = landing?.target || null;
    const takeMe = place === 'quikbase' && myPortal ? (
      <button type="button" className="qlt-chip" data-testid="first-visit-take-me" onClick={() => openCourseMenu(myPortal)}>
        Take me to my course
      </button>
    ) : null;
    const elsewhere = active && current && current.id !== 'done' && !badgePlaying ? elsewhereGuide(current.id, place) : null;
    if (elsewhere) {
      return (
        <>
          {elsewhere.target ? <FocusRing targetId={elsewhere.target} onMissing={openControls} /> : null}
          {place === 'quikbase' ? (
            <Spotlight title="Walk into the portal" body="Find the green portal and walk into it to continue your course.">{takeMe}</Spotlight>
          ) : (
            <NextStepCard icon="leave" title={elsewhere.text} />
          )}
        </>
      );
    }
    if (!here || badgePlaying || current.kind === 'badge' || micOpen) return null;
    const tutorName = entry?.tutor || 'your tutor';

    if (current.kind === 'panel') {
      if (current.panel === 'done') return <TourDonePanel token={token} onExplore={exploreMore} />;
      if (current.panel === 'quiz' && entry) return <TourQuizBoard entry={entry} onDone={() => dispatch('quiz:done')} />;
      return null;
    }

    if (place === 'quikbase') {
      const pad = quikbaseTour && !unitMenuOpen ? <MoveKeysPad hint={KEY_STEPS[current.id]?.[1] || null} allow={keyAllow} /> : null;
      const keyStep = KEY_STEPS[current.id];
      if (keyStep) {
        return (
          <>
            {pad}
            <Spotlight targetId="first-visit-keys" side="top" step={keyStep[0]} total={7} title={keyStep[2]} body={keyStep[3]} />
          </>
        );
      }
      if (current.id === 'look') {
        return (
          <>
            {pad}
            <LookHint />
            <Spotlight targetId="first-visit-look" side="below" dim={false} step={6} total={7} title="Look around">
              <div className="qlt-coach-body">Mouse: hold the left button and drag.</div>
              <div className="qlt-coach-or">OR</div>
              <div className="qlt-coach-body">Trackpad: press down and slide your finger.</div>
            </Spotlight>
          </>
        );
      }
      const fly = FLY_STEPS[current.id];
      if (fly) {
        return (
          <>
            {pad}
            <FocusRing targetId={fly[0]} onMissing={openControls} />
            <Spotlight targetId={fly[0]} side="left" handRoom step={7} total={7} title={fly[1]} body={fly[2]} />
          </>
        );
      }
      if (current.id === 'go_portal') {
        // Another course's portal: Unit 1 there is not the learner's course (a Free learner meets the paywall).
        if (unitMenuOpen && myPortal && menuCourse && !samePortal(menuCourse, myPortal)) {
          return <Spotlight title="This portal is for another course">{takeMe}</Spotlight>;
        }
        if (unitMenuOpen) {
          return (
            <>
              <FocusRing targetId={unitTeleportTarget(1)} />
              <Spotlight targetId={unitTeleportTarget(1)} side="below" pad={8} title="Your first unit" body="Click Teleport to go to your world." />
            </>
          );
        }
        // Owner round 13.11: no key forced, no guess about where the learner faces - name the goal (Sol round13 R2 A),
        // with "Take me to my course" for a learner who cannot find it (Sol dev-port D3 A).
        return (
          <>
            {pad}
            <Spotlight title="Walk into the portal" body="Find the green portal and walk into it.">{takeMe}</Spotlight>
          </>
        );
      }
      return pad;
    }

    // the course world: the control to click pulses with the hand; a "What's next" card names it while nothing covers
    // the world (a menu, the store, the plans or the leave sheet open: the outline and the hand alone)
    const overlayOpen = lessonMenuOpen || storeOpen || plansOpen || confirmOpen || unitMenuOpen;
    if (current.kind === 'click') {
      const target = targetOf(current, lessonId);
      const card = NEXT_CARD[current.id];
      return (
        <>
          <FocusRing targetId={target} onMissing={openControls} />
          {card && !overlayOpen && !tutorOn ? <NextStepCard icon={card[0]} title={card[1].replace('{tutor}', tutorName)} /> : null}
        </>
      );
    }
    // the tutor's own bar carries the questions (Try saying with a microphone); the lesson and its closing question speak
    // for themselves
    return null;
  };

  const renderGuide = () => {
  if (JOURNEY_VERSION === 2) return renderGuideV2();
  const elsewhere = active && current && current.id !== 'done' && !badgePlaying ? elsewhereGuide(current.id, place) : null;
    // A shortcut to the learner's course portal's unit menu (Sol tour-first-entry C), offered on every portal step
    // because the controls steps turn the learner towards a row of identical portals (Sol tour-which-portal C).
    const myPortal = landing?.target || null;
    const myPortalName = portalLabel(myPortal);
    const takeMe =
      place === 'quikbase' && myPortal ? (
        <TouchableOpacity
          testID="first-visit-take-me"
          accessibilityRole="button"
          activeOpacity={0.85}
          onPress={() => openCourseMenu(myPortal)}
          style={[styles.chip, { alignSelf: 'flex-start', borderRadius: rem(18), paddingVertical: rem(8), paddingHorizontal: rem(14), marginTop: rem(10) }]}>
          <Text style={[styles.chipText, { fontSize: rem(TYPE.base), fontFamily: FONTS.body }]}>Take me to my course</Text>
        </TouchableOpacity>
      ) : null;
    if (elsewhere) {
      return (
        <>
          {elsewhere.target ? <FocusRing targetId={elsewhere.target} onMissing={openControls} /> : null}
          <CoachCard
            text={place === 'quikbase' && myPortalName ? `Walk into the ${myPortalName} portal to continue your course` : elsewhere.text}
            top={guideTop}
            onSkip={coachSkip}>
            {takeMe}
          </CoachCard>
        </>
      );
    }
    if (!here) return null;
    const tutorName = entry?.tutor || 'your tutor';
    // Clicking TUTOR first asks how the tutor should reply; point at Voice (voice-first, Chat to type) until one is picked.
    const choosing = current.id === 'tutor_open' && tutorChoiceOpen;
    // Without the Upgrade steps (no Free plan), Exit carries their "Well done!".
    const stepText = current.praisedText && !plan?.isFree ? current.praisedText : current.text;
    const text = choosing
      ? `Choose Voice to talk with ${tutorName}, or Chat to type`
      : stepText ? stepText.replace('{tutor}', tutorName) : '';

    if (current.kind === 'panel') {
      if (current.panel === 'done') return badgePlaying ? null : <TourDonePanel token={token} onExplore={exploreMore} />;
      if (current.panel === 'quiz' && entry) return <TourQuizPanel entry={entry} onDone={() => dispatch('quiz:done')} />;
      return null;
    }
    // One thing at a time: nothing over a badge moment.
    if (badgePlaying || current.kind === 'badge') return null;

    if (current.kind === 'click') {
      const target = choosing ? TARGET.tutorModeVoice : targetOf(current, lessonId);
      return (
        <>
          <FocusRing targetId={target} onMissing={openControls} />
          <CoachCard text={text} top={guideTop} onSkip={coachSkip} />
        </>
      );
    }
    if (current.kind === 'key') return <CoachCard text={text} keyCap={current.key} top={guideTop} onSkip={coachSkip} />;
    if (current.suggestions && entry?.tutorChat?.suggestions?.length) {
      return (
        <CoachCard text={`${text}. Try one of these:`} top={guideTop} onSkip={coachSkip}>
          <View style={[styles.chips, { marginTop: rem(10) }]}>
            {entry.tutorChat.suggestions.map(({ question }) => (
              <TouchableOpacity
                key={question}
                testID="first-visit-suggestion"
                activeOpacity={0.85}
                onPress={() => emit('tutor-ask', question)}
                style={[styles.chip, { borderRadius: rem(18), paddingVertical: rem(8), paddingHorizontal: rem(14), margin: rem(4) }]}>
                <Text style={[styles.chipText, { fontSize: rem(TYPE.base), fontFamily: FONTS.body }]}>{question}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </CoachCard>
      );
    }
    // The lesson's closing question: nothing until its tutor is on, so the guide never names a question not asked.
    if (current.id === 'lesson_tutor' && !tutorOn) return null;
    if (current.id === 'go_portal') {
      // Another course's portal: Unit 1 there is not the learner's course (a Free learner meets the paywall).
      if (unitMenuOpen && myPortal && menuCourse && !samePortal(menuCourse, myPortal)) {
        return (
          <CoachCard text="This portal is for another course" top={guideTop} onSkip={coachSkip}>
            {takeMe}
          </CoachCard>
        );
      }
      // Their course's unit menu is open (walked in, or "Take me to my course"): the next move is in that menu.
      if (unitMenuOpen) {
        return (
          <>
            <FocusRing targetId={unitTeleportTarget(1)} />
            <CoachCard text="Click Teleport on Unit 1 to start your course" top={guideTop} onSkip={coachSkip} />
          </>
        );
      }
      return (
        <CoachCard
          text={myPortalName ? `Walk into the ${myPortalName} portal to start your course` : text}
          top={guideTop}
          onSkip={coachSkip}>
          {takeMe}
        </CoachCard>
      );
    }
    return <CoachCard text={text} top={guideTop} onSkip={coachSkip} />;

  };
  const leaveGuide = JOURNEY_VERSION === 2 && place === 'world' && !leadingInWorld ? (
    <LeaveGuide
      tutorOn={!!tutorOn}
      lessonStarted={!!lessonStarted}
      lessonDone={Number(lessonProgress.currentLessonProgressPercentage) >= 100}
      confirmOpen={!!confirmOpen}
    />
  ) : null;
  const guide = leaveGuide || renderGuide();
  // SKIP TOUR at the top left whenever the guided visit is running (owner 10-07; never on sign-up, name or grade).
  // only where the current step is: a step waiting in another place shows nothing here, Skip tour included (Sol C)
  const showSkip = JOURNEY_VERSION === 2 && here && !badgePlaying;
  const tip = blockedTip ? <BlockedTip key={blockedTip.key} rect={blockedTip.rect} /> : null;
  const burst = coinBurst || tip ? (
    <>
      {coinBurst ? <CoinBurst key={coinBurst.key} amount={coinBurst.amount} onDone={clearCoinBurst} /> : null}
      {tip}
    </>
  ) : null;
  if (!showSkip) {
    return burst ? (
      <>
        {guide}
        {burst}
      </>
    ) : guide;
  }
  return (
    <>
      {guide}
      {burst}
      <TouchableOpacity
        testID="first-visit-skip-top"
        accessibilityRole="button"
        accessibilityLabel="Skip tour"
        activeOpacity={0.85}
        onPress={skipTour}
        style={[styles.skipTop, { top: rem(14), left: rem(56), borderRadius: rem(10), paddingVertical: rem(8), paddingHorizontal: rem(14) }]}>
        <Text style={[styles.skipTopText, { fontSize: rem(TYPE.sm), fontFamily: FONTS.body }]}>SKIP TOUR</Text>
      </TouchableOpacity>
    </>
  );
}

/**
 * After the tour, in any visit to a unit world (owner round 13.14; Sol round13 R4 A): once a lesson there is finished
 * and the tutor has been asked a question, turning the tutor off points at the red "Leave unit" - and the sheet it opens
 * at "Back to my courses". Gone once the learner leaves or starts another lesson.
 */
function LeaveGuide({ tutorOn, lessonStarted, lessonDone, confirmOpen }) {
  const [finished, setFinished] = useState(false);
  const [asked, setAsked] = useState(false);
  const [show, setShow] = useState(false);
  useEffect(() => on('lesson-finished', () => setFinished(true)), []);
  useEffect(() => on('tutor-asked', () => setAsked(true)), []);
  useEffect(() => {
    if (lessonDone) setFinished(true);
  }, [lessonDone]);
  const wasOn = useRef(tutorOn);
  useEffect(() => {
    if (wasOn.current && !tutorOn && finished && asked) setShow(true);
    wasOn.current = tutorOn;
  }, [asked, finished, tutorOn]);
  useEffect(() => {
    if (lessonStarted) setShow(false);
  }, [lessonStarted]);
  useEffect(() => {
    setLeaveHome(show);
    return () => setLeaveHome(false);
  }, [show]);
  if (!show) return null;
  return (
    <>
      <FocusRing targetId={TARGET.exit} />
      {!confirmOpen ? <NextStepCard icon="leave" title="Leave the unit" /> : null}
    </>
  );
}

/** "Available after the tour", next to the button that was pressed (below it in the top half, above it lower down). */
function BlockedTip({ rect }) {
  const { rem } = useRemScale();
  const width = rem(210);
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 800;
  const left = Math.max(rem(8), Math.min(viewportWidth - width - rem(8), rect.left + rect.width / 2 - width / 2));
  const below = rect.top + rect.height / 2 < viewportHeight / 2;
  const place = below ? { top: rect.top + rect.height + rem(8) } : { top: Math.max(rem(8), rect.top - rem(44)) };
  return (
    <View pointerEvents="none" testID="first-visit-blocked" style={[styles.blocked, place, { left, width, borderRadius: rem(10), paddingVertical: rem(8) }]}>
      <Text style={[styles.blockedText, { fontSize: rem(TYPE.sm), fontFamily: FONTS.body }]}>Available after the tour</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  blocked: { position: 'fixed', zIndex: 97, alignItems: 'center', backgroundColor: 'rgba(15,16,20,0.92)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.25)' },
  blockedText: { color: '#F6FBFF', fontWeight: '700' },
  skipTop: { position: 'absolute', zIndex: 96, backgroundColor: 'rgba(15,16,20,0.82)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.22)' },
  skipTopText: { color: '#F6FBFF', fontWeight: '700', letterSpacing: 0.6 },
  chips: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center' },
  chip: { borderWidth: 1, borderColor: 'rgba(197,232,54,0.55)', backgroundColor: 'rgba(197,232,54,0.10)' },
  chipText: { color: '#F6FBFF', fontWeight: '600' },
});
